import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../components/context/AuthProvider";
import { Button, Input, Select, LoadingState, ErrorBoundary } from "../../../components/ui";
import AssetMarkdownField from "../explorer/AssetMarkdownField";
import { AssetTableItem } from "../../../types/types";
import { useFormValidation } from "../../../hooks";
import { commonValidationRules } from "../../../utils/validation";
import { AssetTypeOptions } from "@/options/options";
import { apiFetch, PaywallError } from "../../../services/apiClient";
import { PaywallModal } from "../../../components/ui";

interface CreateAssetForm {
  name: string;
  type: string;
  category: string;
  description: string;
  parentId: string;
}

const CreateAssetPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [availableAssets, setAvailableAssets] = useState<AssetTableItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<{ resource: string; limit: number } | null>(null);

  const form = useFormValidation<CreateAssetForm>({
    initialValues: {
      name: "",
      type: "",
      category: "",
      description: "",
      parentId: searchParams.get("parentId") || "", // Pre-fill if coming from parent asset
    },
    validationRules: {
      name: commonValidationRules.name,
      type: commonValidationRules.required,
      category: commonValidationRules.required,
      description: commonValidationRules.description,
    },
    onSubmit: async (values) => {
      try {
        setError(null);
        // Prepare the payload
        const payload = {
          ...values,
          parentId: values.parentId ? parseInt(values.parentId) : null,
        };

        const response = await apiFetch("/api/protected/assets/asset", {
          method: "POST",
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const r = await response.json();
          navigate(`/protected/assets/${r.id}`);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create asset");
        }
      } catch (error) {
        if (error instanceof PaywallError) {
          setPaywall({ resource: error.resource, limit: error.limit });
          return;
        }
        console.error("Error creating asset:", error);
        setError("Failed to create asset. Please try again.");
        throw error;
      }
    },
  });

  // Fetch available assets for parent selection
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch("/api/protected/assets", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.token()}`,
          },
        });
        if (response.ok) {
          const assets = await response.json();
          setAvailableAssets(assets);
        } else {
          throw new Error("Failed to fetch assets");
        }
      } catch (error) {
        console.error("Error fetching assets:", error);
        setError("Failed to load available assets. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [auth]);

  // Add loading and error handling
  if (loading && availableAssets.length === 0) {
    return <LoadingState message="Loading assets..." size="md" fullHeight />;
  }

  if (error) {
    return (
      <div className="p-6 bg-zinc-950 text-white min-h-screen">
        <h1 className="text-2xl font-light tracking-wide mb-6">Create Asset</h1>
        <div className="mb-4 text-red-400">{error}</div>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950 text-white min-h-screen p-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-zinc-900 border border-zinc-800 p-8">
          <h1 className="text-2xl font-light tracking-wide mb-8">Create Entry</h1>
          <ErrorBoundary errorMessage="There was an error creating the asset. Please try again.">
            <form onSubmit={form.handleSubmit} className="space-y-6">
              <Input
                label="Name"
                type="text"
                name="name"
                value={form.values.name}
                onChange={form.handleChange("name")}
                onBlur={form.handleBlur("name")}
                error={form.errors.name}
                fullWidth
                variant="filled"
              />
              <Select
                label="Type"
                name="type"
                value={form.values.type}
                onChange={form.handleChange("type")}
                onBlur={form.handleBlur("type")}
                error={form.errors.type}
                fullWidth
                variant="filled"
                options={AssetTypeOptions}
              />
              <Input
                label="Category"
                type="text"
                name="category"
                value={form.values.category}
                onChange={form.handleChange("category")}
                onBlur={form.handleBlur("category")}
                error={form.errors.category}
                fullWidth
                variant="filled"
              />
              <div>
                <Select
                  label="Parent Asset (Optional)"
                  name="parentId"
                  value={form.values.parentId}
                  onChange={form.handleChange("parentId")}
                  onBlur={form.handleBlur("parentId")}
                  error={form.errors.parentId}
                  disabled={loading}
                  fullWidth
                  variant="filled"
                  options={[
                    { value: "", label: "No Parent (Root Entry)" },
                    ...availableAssets.map((asset) => ({
                      value: asset.id.toString(),
                      label: `${asset.name} (${asset.type})`,
                    })),
                  ]}
                />
                {loading && <LoadingState message="Loading available assets..." size="sm" />}
              </div>
              <div>
                <label className="block text-sm text-amber-400 tracking-wide mb-2">
                  Description
                </label>
                <AssetMarkdownField
                  value={form.values.description}
                  onChange={(value: string) =>
                    form.handleChange("description")({
                      target: { name: "description", value },
                    } as React.ChangeEvent<HTMLTextAreaElement>)
                  }
                  allAssets={availableAssets}
                  onReferenceNavigate={() => {}}
                />
                {form.errors.description && (
                  <div className="text-red-400 text-xs mt-1">{form.errors.description}</div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={form.isSubmitting}
                  loading={form.isSubmitting}
                >
                  {form.isSubmitting ? "Creating..." : "Save and Edit"}
                </Button>
              </div>
            </form>
          </ErrorBoundary>
        </div>
      </div>
      <PaywallModal
        open={!!paywall}
        resource={paywall?.resource ?? ""}
        limit={paywall?.limit ?? 0}
        onClose={() => setPaywall(null)}
      />
    </div>
  );
};

export default CreateAssetPage;
